import { useEffect, useMemo, useState } from "react";
import Editor from "react-simple-wysiwyg";
import {
  createCamelCaseId,
  formatMoney,
  formatMoneyInInputBox,
  NAIRA_SYMBOL,
} from "../../../utils";
import { TicketTypeRawData } from "../../../utils/types";

export function CreateTicketModal({
  handleSaveTicket,
  selectedTicketToEdit,
  handleSaveEditedTicket,
}: {
  handleSaveTicket: (ticket: TicketTypeRawData) => void;
  selectedTicketToEdit: TicketTypeRawData | null;
  handleSaveEditedTicket: (ticket: TicketTypeRawData) => void;
}) {
  const newTicket = {
    title: selectedTicketToEdit?.title || "",
    description: selectedTicketToEdit?.description || "",
    price: selectedTicketToEdit?.price || 0,
    noOfTickets: selectedTicketToEdit?.noOfTickets || 0,
    isFree: selectedTicketToEdit?.isFree || true,
    isUnlimitedQuantity: selectedTicketToEdit?.isUnlimitedQuantity || true,
    isUnlimitedMaxBuy: selectedTicketToEdit?.isUnlimitedMaxBuy || true,
    maxBuy: selectedTicketToEdit?.maxBuy || 0,
    groupSize: selectedTicketToEdit?.groupSize || 0, // for
  };
  const [ticketType, setTicketType] = useState("single");
  const [ticketStructure, setTicketStructure] = useState<TicketTypeRawData>({
    ...newTicket,
  });

  useEffect(() => {
    setTicketStructure(newTicket);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketType, selectedTicketToEdit]);

  const handleEditTicket = (body: any) => {
    body.id = selectedTicketToEdit?.id;
    handleSaveEditedTicket(body);
    document.getElementById("close-create-ticket-modal")?.click();
  };

  const handleCreateTicket = () => {
    const body: any = {
      title: ticketStructure.title,
      description: ticketStructure.description,
      price: ticketStructure.price,
    };
    if (ticketStructure.maxBuy) body.maxBuy = ticketStructure.maxBuy;
    if (ticketStructure.groupSize) body.for = ticketStructure.groupSize;
    if (ticketStructure.noOfTickets)
      body.noOfTickets = ticketStructure.noOfTickets;
    if (selectedTicketToEdit) return handleEditTicket(body);

    body.id = createCamelCaseId(ticketStructure.title);
    handleSaveTicket(body);
    setTicketStructure(newTicket);
    document.getElementById("close-create-ticket-modal")?.click();
  };

  const disableCreateButton = useMemo(() => {
    return (
      !ticketStructure.title ||
      !ticketStructure.description ||
      (!ticketStructure.price && !ticketStructure.isFree) ||
      (!ticketStructure.noOfTickets && !ticketStructure.isUnlimitedQuantity) ||
      (ticketType === "single" &&
        !ticketStructure.maxBuy &&
        !ticketStructure.isUnlimitedMaxBuy) ||
      (ticketType === "group" && !ticketStructure.groupSize)
    );
  }, [ticketStructure, ticketType]);

  return (
    <>
      <div
        className="modal fade create-ticket-modal"
        id="create-ticket-modal"
        tabIndex={-1}
        aria-labelledby="create-ticket-modal-label"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="create-ticket-modal-label">
                Add Ticket
              </h1>
              <button
                type="button"
                className="bttn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                id="close-create-ticket-modal"
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="tickets-category">
                <button
                  onClick={() => setTicketType("single")}
                  className={ticketType === "single" ? "active" : ""}
                >
                  Single
                </button>
                <button
                  onClick={() => setTicketType("group")}
                  className={ticketType === "group" ? "active" : ""}
                >
                  Group
                </button>
              </div>
              <div className="tickets-form">
                <div className="form-group">
                  <label htmlFor="ticket-name">
                    Ticket Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="ticket-name"
                    value={ticketStructure.title}
                    onChange={(e) =>
                      setTicketStructure({
                        ...ticketStructure,
                        title: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ticket-price">Ticket Description</label>
                  <Editor
                    value={ticketStructure.description}
                    onChange={(e) =>
                      setTicketStructure({
                        ...ticketStructure,
                        description: e.target.value,
                      })
                    }
                    title="Ticket description"
                  />
                </div>
                <div className="ticket-price">
                  <div className="form-group">
                    <label htmlFor="ticket-price">
                      Price per ticket <span className="required">*</span>
                    </label>
                    <div
                      className={`custom-input ${
                        ticketStructure.isFree ? "disabled" : ""
                      }`}
                    >
                      <input
                        id="ticket-price"
                        placeholder="₦ 0.00"
                        disabled={ticketStructure.isFree}
                        onChange={(e) =>
                          setTicketStructure({
                            ...ticketStructure,
                            price: formatMoneyInInputBox(e.target.value),
                          })
                        }
                        value={
                          ticketStructure.price > 0
                            ? `${NAIRA_SYMBOL} ${formatMoney(
                                ticketStructure.price
                              )}`
                            : ""
                        }
                      />
                      <div className="country">
                        <img
                          alt="Nigerias Flag"
                          src="http://purecatamphetamine.github.io/country-flag-icons/3x2/NG.svg"
                        />
                        <span>NGN</span>
                      </div>
                    </div>
                    <p>
                      <input
                        type="checkbox"
                        disabled={ticketStructure.isFree}
                      />
                      <span>
                        Push ticket processing charge to buyers.{" "}
                        <b>Learn more</b>
                      </span>
                    </p>
                  </div>
                  <div className="price-container">
                    <div className="form-check form-switch form-check-reverse">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="switchCheckReverse"
                        aria-label="switch-for-isPriceFree"
                        checked={ticketStructure.isFree}
                        onChange={(e) => {
                          const body = {
                            ...ticketStructure,
                            isFree: e.target.checked,
                          };
                          if (!!e.target.checked) body.price = 0;
                          setTicketStructure(body);
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="switchCheckReverse"
                      >
                        is Free
                      </label>
                    </div>
                  </div>
                </div>
                <div className="ticket-quantity">
                  <div className="form-group">
                    <label htmlFor="ticket-quantity">
                      Ticket quantity <span className="required">*</span>
                    </label>
                    <input
                      id="ticket-quantity"
                      placeholder="Number of tickets available"
                      disabled={ticketStructure.isUnlimitedQuantity}
                      value={
                        ticketStructure.noOfTickets > 0
                          ? `${formatMoney(ticketStructure.noOfTickets)}`
                          : ""
                      }
                      onChange={(e) =>
                        setTicketStructure({
                          ...ticketStructure,
                          noOfTickets: formatMoneyInInputBox(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="price-container">
                    <div className="form-check form-switch form-check-reverse">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="switchCheckReverse"
                        aria-label="switch-for-tickets-unlimited"
                        checked={ticketStructure.isUnlimitedQuantity}
                        onChange={(e) => {
                          const body = {
                            ...ticketStructure,
                            isUnlimitedQuantity: e.target.checked,
                          };
                          if (!!e.target.checked) body.noOfTickets = 0;
                          setTicketStructure(body);
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="switchCheckReverse"
                      >
                        is Unlimited
                      </label>
                    </div>
                  </div>
                </div>
                {ticketType === "single" ? (
                  <div className="ticket-quantity">
                    <div className="form-group">
                      <label htmlFor="ticket-quantity">
                        Ticket limit per order{" "}
                        <span className="required">*</span>
                      </label>
                      <input
                        id="ticket-quantity"
                        placeholder="Ticket limit per order"
                        disabled={ticketStructure.isUnlimitedMaxBuy}
                        value={
                          ticketStructure.maxBuy > 0
                            ? `${formatMoney(ticketStructure.maxBuy)}`
                            : ""
                        }
                        onChange={({ target: { value } }) =>
                          setTicketStructure({
                            ...ticketStructure,
                            maxBuy: formatMoneyInInputBox(value),
                          })
                        }
                      />
                    </div>
                    <div className="price-container">
                      <div className="form-check form-switch form-check-reverse">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="switchCheckReverse"
                          aria-label="switch-for-isUnlimitedOrderQuantity"
                          checked={ticketStructure.isUnlimitedMaxBuy}
                          onChange={(e) => {
                            const body = {
                              ...ticketStructure,
                              isUnlimitedMaxBuy: e.target.checked,
                            };
                            if (!!e.target.checked) body.maxBuy = 0;
                            setTicketStructure(body);
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="switchCheckReverse"
                        >
                          is Unlimited
                        </label>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="ticket-quantity">
                    <div className="form-group">
                      <label htmlFor="ticket-quantity">
                        Group size <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        id="ticket-quantity"
                        placeholder="Number of people in a group"
                        value={
                          ticketStructure.groupSize > 0
                            ? `${formatMoney(ticketStructure.groupSize)}`
                            : ""
                        }
                        onChange={({ target: { value } }) =>
                          setTicketStructure({
                            ...ticketStructure,
                            groupSize: formatMoneyInInputBox(value),
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="create-ticket-btn-container">
                <button
                  disabled={disableCreateButton}
                  className="create-ticket-btn"
                  onClick={handleCreateTicket}
                >
                  Add Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
